import { UUID } from "src/types/shared.type";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StoreEvent } from "src/interfaces/event.interface";
import { NOT_FOUND_EVENT_INDEX } from "src/constants/constants";
import type { MenuListActiveFilter } from "src/constants/menuListActiveFilter";

interface EventsState {
  events: StoreEvent[];
  filteredEvents: StoreEvent[];
  toBeDeletedEventId: UUID | null;
  listActiveFilter: MenuListActiveFilter;
}

const initialState: EventsState = {
  events: [],
  filteredEvents: [],
  toBeDeletedEventId: null,
  listActiveFilter: "ACTIVE",
};

export const eventsSlice = createSlice({
  name: "events",
  initialState,
  reducers: {
    set_events: (state, action: PayloadAction<StoreEvent[]>) => {
      state.events = action.payload;
      state.filteredEvents = action.payload;
    },
    
    add_event: (state, action: PayloadAction<StoreEvent>) => {
      state.events.push(action.payload);
      state.filteredEvents.push(action.payload);
    },
    
    update_event: (state, action: PayloadAction<StoreEvent>) => {
      const eventIndex = state.events.findIndex(
        event => event.correlationId === action.payload.correlationId
      );
      if (eventIndex !== NOT_FOUND_EVENT_INDEX) {
        state.events[eventIndex] = {
          ...state.events[eventIndex],
          ...action.payload,
        };
      }

      const filteredEventIndex = state.filteredEvents.findIndex(
        event => event.correlationId === action.payload.correlationId
      );
      if (filteredEventIndex !== NOT_FOUND_EVENT_INDEX) {
        state.filteredEvents[filteredEventIndex] = {
          ...state.filteredEvents[filteredEventIndex],
          ...action.payload,
        };
      }
    },
    
    remove_event: (state, action: PayloadAction<UUID>) => {
      state.events = state.events.filter(
        event => event.correlationId !== action.payload
      );
      state.filteredEvents = state.filteredEvents.filter(
        event => event.correlationId !== action.payload
      );
    },
    
    // Search & Filter
    search_events: (state, action: PayloadAction<string>) => {
      const searchTerm = action.payload?.toLowerCase() || "";
      if (!searchTerm) {
        state.filteredEvents = state.events;
      } else {
        state.filteredEvents = state.events.filter(
          event => event.eventName?.toLowerCase().includes(searchTerm) ?? false
        );
      }
    },
    
    sort_events: (state, action: PayloadAction<'aToZ' | 'zToA'>) => {
      const sortedEvents = [...state.filteredEvents].sort((eventA, eventB) => {
        const eventNameA = eventA.eventName?.toLowerCase() ?? '';
        const eventNameB = eventB.eventName?.toLowerCase() ?? '';
        
        if (action.payload === 'aToZ') {
          return eventNameA.localeCompare(eventNameB);
        } else {
          return eventNameB.localeCompare(eventNameA);
        }
      });
      
      state.filteredEvents = sortedEvents;
    },
    
    set_delete_modal_event_id: (state, action: PayloadAction<UUID | null>) => {
      state.toBeDeletedEventId = action.payload;
    },

    set_events_list_active_filter: (state, action: PayloadAction<MenuListActiveFilter>) => {
      state.listActiveFilter = action.payload;
    },
  },
});

export const {
  set_events,
  add_event,
  update_event,
  remove_event,
  search_events,
  sort_events,
  set_delete_modal_event_id,
  set_events_list_active_filter,
} = eventsSlice.actions;

export default eventsSlice.reducer;

